// The conversation the intake ends on: what Keni says, and where each answer
// leads. See ProcessingChatScreen.
//
// Its own module rather than sitting on the screen, for two reasons. It is
// copy, which is what a content module is for. And a file that default-exports
// a component and also exports something else cannot Fast Refresh: every edit
// invalidated the module, took App.tsx down with it and reloaded the whole app,
// which looked like the screens not working.

export type TurnId =
  'open' | 'invite' | 'explain' | 'email' | 'invited' | 'later' | 'alone';

type Turn = {
  /** What Keni says, one bubble each, typed in order. */
  says: string[];
  /** How it can be answered. A turn with none is the end of the road. */
  replies?: { label: string; icon: string; to: TurnId }[];
};

/**
 * The conversation, as a script.
 *
 * A map of turns rather than nested state: the interesting thing about this
 * screen is which questions lead where, and that should be readable in one
 * place rather than assembled from conditions scattered through a render.
 *
 * "What's a care circle?" is a turn like any other rather than a help sheet.
 * Somebody who does not know what we are asking about is still in the
 * conversation, and lifting them out of it to explain a term is how a chat
 * becomes a form with tooltips.
 */
/**
 * What the app bar says over this chat. What is happening rather than a journey
 * name, and it matches the notice at the end that says the reading is done.
 */
export const PROCESSING_BAR = 'While we read everything';

const INVITE_REPLY = {
  label: 'Invite to care circle',
  icon: 'user-plus',
  to: 'email',
} as const;
const EXPLAIN_REPLY = {
  label: 'What’s a care circle?',
  icon: 'circle-question',
  to: 'explain',
} as const;

export const SCRIPT: Record<TurnId, Turn> = {
  open: {
    says: [
      'Thanks, we’re processing everything you’ve told us. We’ll let you know when it’s ready.',
      'Is there anyone else that helps you with your caring responsibilities?',
    ],
    replies: [
      { label: 'Yes, someone helps', icon: 'check', to: 'invite' },
      { label: 'No, it’s just me', icon: 'xmark', to: 'alone' },
    ],
  },
  // No "No": not inviting anyone is just not tapping, and the reading finishing
  // is the way on either way. The two rows stay after an invite, so a second
  // person is the same tap as the first.
  invite: {
    says: ['Would you like to invite them to your care circle?'],
    replies: [
      INVITE_REPLY,
      // A reply rather than a link on the question: not knowing the term is a
      // reasonable answer to it, and the other two both assume you do.
      EXPLAIN_REPLY,
    ],
  },
  explain: {
    // The circle already exists: it is them and the person they care for, from
    // the moment they sign up. Adding somebody is not what creates it, it is
    // what gets that person into the app, and saying otherwise made a thing
    // they already have sound like a thing to set up.
    says: [
      'A care circle is you, the person you’re caring for, and then anyone else you want to add. They get access to the app as well.',
      'Would you like to add someone?',
    ],
    replies: [INVITE_REPLY],
  },
  // Nothing said: "Yes" opens the invite sheet, and the chat waits under it.
  email: {
    says: [],
  },
  invited: {
    says: ['They’ll get an invite to join your care circle.'],
    replies: [INVITE_REPLY, EXPLAIN_REPLY],
  },
  later: {
    says: ['No problem. You can add people any time from your care circle.'],
  },
  alone: {
    // Says what happens next as well: with nobody to invite, this is the end of
    // the conversation, and the ping is the next thing they will hear.
    says: [
      'Thanks. You can add someone later if that changes.',
      'We’ll ping you when we’ve finished reading everything.',
    ],
  },
};

/** What the chat says about an invite. The sheet is the care circle's own. */
export const INVITE = {
  /** Keni's confirmation, naming who it went to. */
  sent: (email: string) => `We’ve sent an invite to ${email}.`,
};

/** The notice, for the screen where the reading comes back. */
export const DONE = {
  title: 'We’ve finished reading everything you told us',
  // Not "have a look at what we got": the notice lands mid-conversation, and
  // telling somebody to go and look is telling them to stop talking. This says
  // the review is there when they are ready.
  body: 'You can continue whenever you are finished with your conversation.',
  action: 'Continue to review',
};
